import { useCallback, useEffect, useState } from 'react'

/**
 * The event Chromium fires when a site meets the install criteria. It is not in
 * lib.dom, and it is the only way to trigger a real install dialog.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export interface InstallPromptState {
  /** True when a native install dialog can actually be shown. */
  available: boolean
  installed: boolean
  /** Resolves to whether the user accepted. False when no prompt was available. */
  promptInstall: () => Promise<boolean>
}

/**
 * Captures `beforeinstallprompt` so the install offer can be made at a sensible
 * moment rather than whenever Chrome decides.
 *
 * The listener has to be attached as early as possible: the event fires once,
 * shortly after load, and is gone if nothing preventDefault()s it. That is why
 * this is a module-level capture rather than something set up inside the
 * onboarding component, which mounts too late.
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null
let installed = false
const listeners = new Set<() => void>()

function notify() {
  for (const listener of listeners) listener()
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredPrompt = event as BeforeInstallPromptEvent
    notify()
  })

  window.addEventListener('appinstalled', () => {
    installed = true
    deferredPrompt = null
    notify()
  })
}

export function useInstallPrompt(): InstallPromptState {
  const [, forceRender] = useState(0)

  useEffect(() => {
    const listener = () => forceRender((value) => value + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false

    await deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice

    // The event is single-use: a dismissed prompt cannot be shown again until
    // the browser decides to re-fire it.
    deferredPrompt = null
    notify()

    return outcome === 'accepted'
  }, [])

  return { available: deferredPrompt !== null, installed, promptInstall }
}
